import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Home', href: '/homepage' },
    { label: 'Regional Dive Sites', href: '/regional-dive-sites' },
    { label: 'Dive Centres Directory', href: '/dive-centres-directory' },
    { label: 'Marine Life Guide', href: '/marine-life' },
  ];

  const resourceLinks = [
    { label: 'Cyprus Diving Information', href: '/cyprus-diving-information' },
    { label: 'External Sources', href: '/useful-resources' },
    { label: 'Safety Guidelines', href: '/cyprus-diving-information#safety' },
    { label: 'Conservation Efforts', href: '/cyprus-diving-information#conservation' },
  ];

  const socialLinks = [
    { name: 'Facebook', icon: 'ShareIcon', href: 'https://www.facebook.com/cyprusdivinginfo' },
  ];

  return (
    <footer className={`bg-card border-t border-border ${className}`}>
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="relative w-10 h-10">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                  <circle cx="24" cy="24" r="22" fill="#0B7EC8" opacity="0.1"/>
                  <path d="M24 8C15.163 8 8 15.163 8 24C8 32.837 15.163 40 24 40C32.837 40 40 32.837 40 24C40 15.163 32.837 8 24 8Z" fill="#0B7EC8"/>
                  <path d="M24 12C17.373 12 12 17.373 12 24C12 30.627 17.373 36 24 36C30.627 36 36 30.627 36 24C36 17.373 30.627 12 24 12Z" fill="#40E0D0"/>
                  <ellipse cx="20" cy="22" rx="2" ry="3" fill="#0B7EC8"/>
                  <ellipse cx="28" cy="22" rx="2" ry="3" fill="#0B7EC8"/>
                  <path d="M18 28C18 28 20 30 24 30C28 30 30 28 30 28" stroke="#0B7EC8" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <span className="text-xl font-headline font-bold text-primary">Cyprus Diving</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your independent, community-driven guide to Cyprus' underwater world — built for curious divers and dedicated to marine conservation.
            </p>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-xs text-success bg-success/10 px-3 py-2 rounded-md border-l-4 border-success">
                <Icon name="ShieldCheckIcon" size={16} />
                <span className="font-medium">Independent • Non-Commercial</span>
              </div>
              <a 
                href="https://www.cyprusdiving.info" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-xs text-primary hover:text-primary/80 transition-colors"
              >
                <Icon name="GlobeAltIcon" size={16} />
                <span className="font-medium">www.cyprusdiving.info</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-headline font-semibold text-foreground mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 flex items-center space-x-2 group"
                  >
                    <Icon name="ChevronRightIcon" size={16} className="group-hover:translate-x-1 transition-transform" />
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* External Sources */}
          <div>
            <h3 className="text-lg font-headline font-semibold text-foreground mb-4">External Sources</h3>
            <ul className="space-y-2">
              {resourceLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 flex items-center space-x-2 group"
                  >
                    <Icon name="ChevronRightIcon" size={16} className="group-hover:translate-x-1 transition-transform" />
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Community & Contact */}
          <div>
            <h3 className="text-lg font-headline font-semibold text-foreground mb-4">Contact & Connect</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Got a dive question, a favourite site, or a hidden gem to share? Let's talk bubbles.
            </p>
            <div className="space-y-3 mb-4">
              <div className="space-y-2">
                <a 
                  href="mailto:hello@cyprusdiving.info"
                  className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-primary transition-colors group"
                >
                  <Icon name="EnvelopeIcon" size={16} className="group-hover:scale-110 transition-transform" />
                  <span>hello@cyprusdiving.info</span>
                </a>
                <p className="text-xs text-muted-foreground pl-6">
                  Want your dive centre or favourite site listed? Drop us a message.
                </p>
              </div>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="MapPinIcon" size={16} />
                <span>Cyprus · Mediterranean Sea</span>
              </div>
            </div>
            <a
              href="https://www.facebook.com/cyprusdivinginfo"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-primary transition-colors group"
            >
              <img 
                src="/assets/images/Facebook_Logo_Primary-1769858753863.png" 
                alt="Facebook" 
                className="w-5 h-5 group-hover:scale-110 transition-transform"
              />
              <span>Follow Cyprus Diving</span>
            </a>
          </div>
        </div>

        {/* Disclaimer Banner */}
        <div className="mb-8 p-4 bg-muted/50 border border-border rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="InformationCircleIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground leading-relaxed">
              <p className="font-medium text-foreground mb-1">Independent Information Resource</p>
              <p>
                Cyprus Diving is an independent, non-commercial website dedicated to providing comprehensive diving information for Cyprus. 
                We are not affiliated with any dive centres, tour operators, or commercial entities and do not accept commissions for listings or recommendations. All information is provided for educational 
                and informational purposes to help divers explore Cyprus' underwater world safely and responsibly.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © {currentYear} Cyprus Diving. All rights reserved. Built on salt, bubbles & bad decisions (only above water).
            </p>
            <div className="flex items-center space-x-6">
              <Link href="/privacy-policy/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Privacy Policy
              </Link>
              <Link href="/terms-of-use/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Terms of Use
              </Link>
              <Link href="/accessibility/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Accessibility
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;